import React, { useState, useEffect, useRef } from 'react';
import Icon from '../../../components/AppIcon';
import { supabase } from '../../../lib/supabase';

const AssignmentNotificationBanner = () => {
  const [notifications, setNotifications] = useState([]);
  const subscriptionRef = useRef(null);
  const timerRefs = useRef({});

  useEffect(() => {
    setupSubscription();
    return () => {
      if (subscriptionRef?.current) {
        subscriptionRef?.current?.unsubscribe();
      }
      Object.values(timerRefs?.current)?.forEach(clearTimeout);
    };
  }, []);

  const setupSubscription = () => {
    subscriptionRef.current = supabase?.channel('assignment-notifications')?.on('postgres_changes', {
        event: 'INSERT',
        schema: 'public',
        table: 'ticket_technicians'
      }, async (payload) => {
        // Fetch ticket and technician details for the notification
        const { data: ticketData } = await supabase?.from('tickets')?.select('ticket_number, customers(name)')?.eq('id', payload?.new?.ticket_id)?.single();

        const { data: techData } = await supabase?.from('personnel')?.select('name')?.eq('id', payload?.new?.technician_id)?.single();

        const notifId = `assign-${payload?.new?.id || Date.now()}`;
        const newNotif = {
          id: notifId,
          type: 'assignment',
          title: 'New Assignment',
          message: techData?.name
            ? `${techData?.name} assigned to ${ticketData?.ticket_number || 'ticket'}`
            : `Technician assigned to ${ticketData?.ticket_number || 'ticket'}`,
          customer: ticketData?.customers?.name || null,
          timestamp: new Date()?.toISOString()
        };

        setNotifications(prev => [newNotif, ...prev]?.slice(0, 5));

        // Auto-dismiss after 6 seconds
        timerRefs.current[notifId] = setTimeout(() => {
          dismissNotification(notifId);
        }, 6000);
      })?.on('postgres_changes', {
        event: 'UPDATE',
        schema: 'public',
        table: 'ticket_technicians'
      }, async (payload) => {
        const { data: ticketData } = await supabase?.from('tickets')?.select('ticket_number')?.eq('id', payload?.new?.ticket_id)?.single();

        const notifId = `update-${payload?.new?.id || Date.now()}`;
        const newNotif = {
          id: notifId,
          type: 'update',
          title: 'Assignment Updated',
          message: `Assignment for ${ticketData?.ticket_number || 'ticket'} was updated`,
          timestamp: new Date()?.toISOString()
        };

        setNotifications(prev => [newNotif, ...prev]?.slice(0, 5));
        timerRefs.current[notifId] = setTimeout(() => {
          dismissNotification(notifId);
        }, 5000);
      })?.subscribe();
  };

  const dismissNotification = (id) => {
    setNotifications(prev => prev?.filter(n => n?.id !== id));
    if (timerRefs?.current?.[id]) {
      clearTimeout(timerRefs?.current?.[id]);
      delete timerRefs?.current?.[id];
    }
  };

  if (notifications?.length === 0) return null;

  return (
    <div className="fixed bottom-4 right-4 z-[1800] space-y-2 max-w-sm w-full pointer-events-none">
      {notifications?.map((notif) => (
        <div
          key={notif?.id}
          className={`pointer-events-auto flex items-start gap-3 p-3 rounded-lg shadow-elevation-3 border animate-in slide-in-from-right-4 ${
            notif?.type === 'assignment' ?'bg-card border-primary/30' :'bg-card border-warning/30'
          }`}
        >
          <div className={`w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0 ${
            notif?.type === 'assignment' ? 'bg-primary/10' : 'bg-warning/10'
          }`}>
            <Icon
              name={notif?.type === 'assignment' ? 'UserCheck' : 'RefreshCw'}
              size={16}
              color={notif?.type === 'assignment' ? 'var(--color-primary)' : 'var(--color-warning)'}
            />
          </div>
          <div className="flex-1 min-w-0">
            <p className="text-sm font-semibold text-foreground">{notif?.title}</p>
            <p className="text-xs text-muted-foreground truncate">{notif?.message}</p>
            {notif?.customer && (
              <p className="text-xs text-muted-foreground">Customer: {notif?.customer}</p>
            )}
          </div>
          <button
            onClick={() => dismissNotification(notif?.id)}
            className="p-1 rounded hover:bg-muted transition-smooth flex-shrink-0"
          >
            <Icon name="X" size={14} color="var(--color-muted-foreground)" />
          </button>
        </div>
      ))}
    </div>
  );
};

export default AssignmentNotificationBanner;
